import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { createBrandedData } from '../../../../../schema/type-utils/entry.js';
import { useGetMarketplaceBalance } from '../../marketplace/entry.js';
import { useGetAccountBalance } from '../../user/entry.js';
/**
 * Calculate current balance based on user role and active wallet.
 *
 * This hook consolidates balance calculation logic for players and retailers,
 * handling different wallet types (player wallet vs marketplace draws).
 *
 * @param root0 Configuration options for balance calculation
 * @param root0.activeWallets Active wallets for the coupon
 * @param root0.couponIndex Index of the current coupon
 * @param root0.drawNumber Draw number for marketplace balance
 * @param root0.retailerData Optional retailer balance
 * @param root0.product Product for marketplace balance
 * @param root0.contextWallets Optional wallets from context, used for real-time balance updates
 * @param root0.role User role to determine balance source
 * @returns Balance amount and redirect flag
 */
export function useBalance(t0) {
  const $ = _c(21);
  const {
    activeWallets,
    couponIndex,
    drawNumber,
    product,
    retailerData,
    contextWallets,
    role
  } = t0;
  const hasPlayerRole = role === "Player";
  const hasRetailerRole = role === "Retailer";
  let t1;
  if ($[0] !== hasPlayerRole) {
    t1 = {
      defaultValue: error => {
        if (!hasPlayerRole) {
          return {
            id: "WALLET_ID_PLAYER",
            label: "Min spelkassa",
            meta: {
              balance: createBrandedData(0)
            },
            type: "WALLET_PLAYER"
          };
        }
        throw new Error(error.error);
      }
    };
    $[0] = hasPlayerRole;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const playerWallet = useGetAccountBalance(t1);
  let t2;
  if ($[2] !== hasPlayerRole) {
    t2 = {
      defaultValue: error_0 => {
        if (!hasPlayerRole) {
          return {
            activatedDraws: []
          };
        }
        throw new Error(error_0.error);
      }
    };
    $[2] = hasPlayerRole;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const marketplaceData = useGetMarketplaceBalance(drawNumber, product, t2);
  const walletsToUse = contextWallets ?? marketplaceData.data.activatedDraws;
  let t3;
  bb0: {
    if (hasPlayerRole) {
      const currentWallet = activeWallets?.[couponIndex];
      if (typeof currentWallet === "number") {
        let t4;
        if ($[4] !== currentWallet || $[5] !== walletsToUse) {
          let t5;
          if ($[7] !== currentWallet) {
            t5 = draw => draw.activatedDrawId === currentWallet;
            $[7] = currentWallet;
            $[8] = t5;
          } else {
            t5 = $[8];
          }
          t4 = walletsToUse.find(t5);
          $[4] = currentWallet;
          $[5] = walletsToUse;
          $[6] = t4;
        } else {
          t4 = $[6];
        }
        const activatedDraw = t4;
        const t5 = activatedDraw?.balance ?? 0;
        let t6;
        if ($[9] !== t5) {
          t6 = {
            balance: t5,
            shouldRedirectUser: false
          };
          $[9] = t5;
          $[10] = t6;
        } else {
          t6 = $[10];
        }
        t3 = t6;
        break bb0;
      }
      let t4;
      if ($[11] !== playerWallet.data.meta.balance) {
        t4 = {
          balance: playerWallet.data.meta.balance,
          shouldRedirectUser: false
        };
        $[11] = playerWallet.data.meta.balance;
        $[12] = t4;
      } else {
        t4 = $[12];
      }
      t3 = t4;
      break bb0;
    }
    if (hasRetailerRole) {
      let t4;
      if ($[13] !== retailerData?.activatedDraw?.currentPlayableAmount) {
        t4 = Number.parseInt(retailerData?.activatedDraw?.currentPlayableAmount ?? "0", 10);
        $[13] = retailerData?.activatedDraw?.currentPlayableAmount;
        $[14] = t4;
      } else {
        t4 = $[14];
      }
      let t5;
      if ($[15] !== t4) {
        t5 = {
          balance: t4,
          shouldRedirectUser: false
        };
        $[15] = t4;
        $[16] = t5;
      } else {
        t5 = $[16];
      }
      t3 = t5;
      break bb0;
    }
    let t4;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        balance: 0,
        shouldRedirectUser: true
      };
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    t3 = t4;
  }
  const {
    balance,
    shouldRedirectUser
  } = t3;
  let t4;
  if ($[18] !== balance || $[19] !== shouldRedirectUser) {
    t4 = {
      balance,
      shouldRedirectUser
    };
    $[18] = balance;
    $[19] = shouldRedirectUser;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  return t4;
}